import type {
  CategoryPlan,
  CategoryColumnLink,
  CategoryGroup,
  OptionMappingWithProvenance,
} from '@app/src/types/categoryPlan'

/** Parse "existing:42" or "new:Risk Level" - handles colons in names */
function parseCategoryKey(key: string): { type: string; id: string } {
  const colonIndex = key.indexOf(':')
  return { type: key.slice(0, colonIndex), id: key.slice(colonIndex + 1) }
}

function isCategoryMatch(link: CategoryColumnLink, key: string): boolean {
  const { type, id } = parseCategoryKey(key)
  return type === 'existing' ? link.existingCategoryId === parseInt(id) : link.suggestedCategoryName === id
}

export function getCategoryKey(link: CategoryColumnLink): string {
  return link.existingCategoryId != null ? `existing:${link.existingCategoryId}` : `new:${link.suggestedCategoryName}`
}

export function getAllCategoryKeys(plan: CategoryPlan): { key: string; label: string }[] {
  const seen = new Set<string>()
  const result: { key: string; label: string }[] = []
  for (const link of plan.links) {
    const key = getCategoryKey(link)
    if (!seen.has(key)) {
      seen.add(key)
      result.push({
        key,
        label: link.suggestedCategoryName ?? `Category #${link.existingCategoryId}`,
      })
    }
  }
  return result
}

export function groupByCategory(plan: CategoryPlan): CategoryGroup[] {
  const groups = new Map<string, CategoryColumnLink[]>()

  for (const link of plan.links) {
    const key = getCategoryKey(link)
    const existing = groups.get(key) ?? []
    existing.push(link)
    groups.set(key, existing)
  }

  return Array.from(groups.values()).map(links => {
    const first = links[0]
    const seenValues = new Map<string, OptionMappingWithProvenance>()

    for (const link of links) {
      for (const opt of link.optionMappings) {
        const normalizedKey = opt.rawValue.toLowerCase().trim()
        const existing = seenValues.get(normalizedKey)
        if (existing) {
          if (!existing.sourceColumns.includes(link.sourceColumnName)) {
            existing.sourceColumns.push(link.sourceColumnName)
          }
        } else {
          seenValues.set(normalizedKey, {
            ...opt,
            sourceColumns: [link.sourceColumnName],
          })
        }
      }
    }

    return {
      existingCategoryId: first.existingCategoryId,
      suggestedCategoryName: first.suggestedCategoryName,
      sourceColumns: [...new Set(links.map(l => l.sourceColumnName))],
      confidenceScore: Math.min(...links.map(l => l.confidenceScore)),
      isEnabled: links.some(l => l.isEnabled),
      replaceMode: links.some(l => l.replaceMode === true),
      optionMappings: Array.from(seenValues.values()),
    }
  })
}

export function setReplaceMode(plan: CategoryPlan, categoryKey: string, replaceMode: boolean): CategoryPlan {
  return {
    links: plan.links.map(link => (isCategoryMatch(link, categoryKey) ? { ...link, replaceMode } : link)),
  }
}

export function renameCategoryName(plan: CategoryPlan, categoryKey: string, newName: string): CategoryPlan {
  return {
    links: plan.links.map(link =>
      isCategoryMatch(link, categoryKey) ? { ...link, suggestedCategoryName: newName } : link,
    ),
  }
}

export function toggleCategory(plan: CategoryPlan, categoryKey: string): CategoryPlan {
  const currentEnabled = plan.links.filter(l => isCategoryMatch(l, categoryKey)).some(l => l.isEnabled)
  return {
    links: plan.links.map(link =>
      isCategoryMatch(link, categoryKey) ? { ...link, isEnabled: !currentEnabled } : link,
    ),
  }
}

export function removeOption(plan: CategoryPlan, rawValue: string, categoryKey: string): CategoryPlan {
  return {
    links: plan.links.map(link =>
      isCategoryMatch(link, categoryKey)
        ? {
            ...link,
            optionMappings: link.optionMappings.map(opt =>
              opt.rawValue.toLowerCase() === rawValue.toLowerCase() ? { ...opt, isRemoved: true } : opt,
            ),
          }
        : link,
    ),
  }
}

export function restoreOption(plan: CategoryPlan, rawValue: string, categoryKey: string): CategoryPlan {
  return {
    links: plan.links.map(link =>
      isCategoryMatch(link, categoryKey)
        ? {
            ...link,
            optionMappings: link.optionMappings.map(opt =>
              opt.rawValue.toLowerCase() === rawValue.toLowerCase() ? { ...opt, isRemoved: false } : opt,
            ),
          }
        : link,
    ),
  }
}

export function renameOption(plan: CategoryPlan, rawValue: string, newName: string, categoryKey: string): CategoryPlan {
  return {
    links: plan.links.map(link =>
      isCategoryMatch(link, categoryKey)
        ? {
            ...link,
            optionMappings: link.optionMappings.map(opt =>
              opt.rawValue.toLowerCase() === rawValue.toLowerCase() ? { ...opt, suggestedOptionName: newName } : opt,
            ),
          }
        : link,
    ),
  }
}

export function moveOption(
  plan: CategoryPlan,
  rawValue: string,
  fromCategoryKey: string,
  targetCategoryKey: string,
): CategoryPlan {
  const { type: toType, id: toId } = parseCategoryKey(targetCategoryKey)

  // Find the option to move and its source column
  const sourceLink = plan.links.find(
    l =>
      isCategoryMatch(l, fromCategoryKey) &&
      l.optionMappings.some(o => o.rawValue.toLowerCase() === rawValue.toLowerCase()),
  )
  if (!sourceLink) return plan

  const movedOption = sourceLink.optionMappings.find(o => o.rawValue.toLowerCase() === rawValue.toLowerCase())
  if (!movedOption) return plan

  const srcColumnName = sourceLink.sourceColumnName

  // Remove from source
  let newLinks = plan.links.map(link =>
    isCategoryMatch(link, fromCategoryKey)
      ? {
          ...link,
          optionMappings: link.optionMappings.filter(o => o.rawValue.toLowerCase() !== rawValue.toLowerCase()),
        }
      : link,
  )

  // Add to target
  const targetLink = newLinks.find(l => isCategoryMatch(l, targetCategoryKey) && l.sourceColumnName === srcColumnName)

  if (targetLink) {
    newLinks = newLinks.map(link =>
      link === targetLink ? { ...link, optionMappings: [...link.optionMappings, { ...movedOption }] } : link,
    )
  } else {
    newLinks.push({
      sourceColumnName: srcColumnName,
      delimiter: sourceLink.delimiter,
      existingCategoryId: toType === 'existing' ? parseInt(toId) : null,
      suggestedCategoryName: toType === 'new' ? toId : null,
      confidenceScore: 0.5,
      optionMappings: [{ ...movedOption }],
      isEnabled: true,
    })
  }

  return { links: newLinks }
}
